'use client';

/**
 * Dependency imports
 */
import React from 'react';

/**
 * Local imports
 */
import { NavigationContext } from '@/context/Navigation';
import { usePathname, useRouter } from 'next/navigation';
import { animate } from 'framer-motion';

/**
 * TransitionLink Component
 */
export interface TransitionLinkProps {
  href: string;
  transition?: boolean;
  noLink?: boolean;
  ariaLabel?: string;
}

export type TransitionLinkCombinedProps = TransitionLinkProps &
  JSX.IntrinsicElements['a'];

const TransitionLink: React.FC<TransitionLinkCombinedProps> = ({
  href,
  transition = true,
  noLink = false,
  ariaLabel,
  rel,
  children,
  ...props
}) => {
  const navigationContext = React.useContext(NavigationContext);
  const router = useRouter();
  const pathname = usePathname();

  const handleClick = (e: React.MouseEvent) => {
    if (transition) {
      e.preventDefault();

      if (pathname !== href) {
        navigationContext.nav(e.clientX, e.clientY, href, router);
      } else {
        animate(window.scrollY, 0, {
          duration: 0.4,
          onUpdate: (v) => {
            window.scrollTo(0, v);
          },
        });
      }
    }
  };

  if (noLink) {
    return (
      <span onClick={handleClick} {...props}>
        {children}
      </span>
    );
  }

  return (
    <a href={href} aria-label={ariaLabel} rel={rel} onClick={handleClick} {...props}>
      {children}
    </a>
  );
};

export default TransitionLink;
